import React, { useEffect, useState, useContext } from 'react';
import {
  PayPalButtons,
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from '@paypal/react-paypal-js';
import { OnApproveData, PayPalButtonOnApprove } from '@paypal/paypal-js';
import { Typography } from '@one-thd/sui-atomic-components';
import {
  ExperienceContext,
  useConfigService,
} from '@thd-nucleus/experience-context';
import { IB2BPaymentCard } from '../../../models/b2b/payments';
import { Alert } from '../../shared/alert/alert';
import { Spinner } from '../../shared/spinner/spinner';
import { usePayments } from '../../../hooks/use-payments';
import { CardType } from '../../../constants/payments';
import { paymentsApi } from '../../../api/common/payments';
import { PaypalCard } from '../paypal-card/paypal-card';
import { IPaymentCardCommonData } from '../../../models/common/payments';
import { isProd } from '../../../utils/environments';
import {
  PAYPAL_LLC_CLIENT_ID,
  PAYPAL_PROD_CLIENT_ID,
} from '../../../constants/paypal';
import { getUserId } from '../../../utils/customer';
import { TAlertStatus } from '../../../models/common/alert-messages';
import {
  CHANGES_SAVED_MSG,
  PAYPAL_ADDED_MSG,
} from '../../../constants/response-success-messages';
import {
  setAddCardAnalytics,
  setEditCardAnalytics,
} from '../../../utils/payments-analytics';
import { Pagination } from '../../shared/pagination/pagination';
import {
  getNewPageNumber,
  getPaymentsErrorCode,
  hasNoPaymentCards,
} from '../../../utils/payments';
import { scrollToTop } from '../../../utils/general';
import {
  ERR_GENERIC,
  ERR_UNABLE_TO_SAVE,
} from '../../../constants/response-error-messages';
import { pushErrorEvent, setOverlayEvent } from '../../../utils/analytics';
import { NoPaymentCardsMessage } from '../../shared/no-payment-cards-message/no-payment-cards-message';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import { CustomerRole } from '../../../models/common/customer';
import { newrelic } from '../../../utils/newrelic';

const payPalClientId = isProd() ? PAYPAL_PROD_CLIENT_ID : PAYPAL_LLC_CLIENT_ID;

const getUserLevelPaypalAccount = (
  isB2B: boolean,
  isAdmin: boolean,
  paymentCards?: IPaymentCardCommonData[],
) => {
  if (!paymentCards?.length) return null;

  return isB2B && isAdmin
    ? paymentCards.find((card) => card.userId === getUserId()) || null
    : paymentCards[0] || null;
};

const PayPalListContent = () => {
  const [deletedCardIndex, setDeletedCardIndex] = useState<number>(-1);
  const hasDeleteIndex = deletedCardIndex > -1;
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const [{ isPending }] = usePayPalScriptReducer();
  const {
    loading,
    paypalCards,
    getPaymentCards,
    isB2B,
    paginationInfo,
    alertMessage,
    currentPageNumber,
    setAlertMessage,
  } = usePayments();
  const { role } = useCustomerInfo();

  const isAdmin = role === CustomerRole.ADMIN;
  const isSavedPaypal = !!getUserLevelPaypalAccount(
    isB2B,
    isAdmin,
    paypalCards?.paymentCards,
  );

  const isPaypalRestEnabled = !!useConfigService('isPaypalRestEnabled');

  const totalPages = Number(paginationInfo?.totalPages) || 0;

  const resetAlert = () => {
    setDeletedCardIndex(-1);
    setAlertMessage(null);
  };

  const onActionFailure = (
    paypalAccountId: string | undefined,
    message: string,
  ) => {
    resetAlert();
    setAlertMessage({
      paymentId: paypalAccountId,
      text: message,
      alertStatus: TAlertStatus.ERROR,
    });
  };

  const onActionSuccess = (
    paypalAccountId: string,
    message: string,
    indexOfCard?: number,
  ) => {
    resetAlert();

    setAlertMessage({
      paymentId: paypalAccountId,
      text: message,
      alertStatus: TAlertStatus.SUCCESS,
    });

    if (indexOfCard !== undefined) {
      setDeletedCardIndex(indexOfCard);
      const newPage = getNewPageNumber(
        currentPageNumber,
        totalPages,
        paginationInfo?.paymentsOnCurrentPage,
      );
      onChangePage(newPage);
      return;
    }

    onChangePage(1);
  };

  const handlePaypalInit = () => {
    setOverlayEvent('add paypal');
    return paymentsApi.paypalInit(isB2B);
  };

  const handlePaypalInitv2 = () => {
    setOverlayEvent('add paypal');
    return paymentsApi.initPayPalV2();
  };

  const onPaypalAddSuccess = (type: 'rest' | 'soap') => {
    newrelic.addPageAction(`paypal-${type}-add`, {
      status: 'success',
    });
    resetAlert();
    setAlertMessage({
      text: PAYPAL_ADDED_MSG.message,
      alertStatus: TAlertStatus.SUCCESS,
    });
    onChangePage(1);
    setAddCardAnalytics('paypal', false, '', '');
  };

  const onPaypalEditSuccess = (paypalAccountId?: string) => {
    newrelic.addPageAction('paypal-edit', {
      status: 'success',
    });
    resetAlert();
    setAlertMessage({
      paymentId: paypalAccountId,
      text: CHANGES_SAVED_MSG.message,
      alertStatus: TAlertStatus.SUCCESS,
    });
    onChangePage(1);
    setEditCardAnalytics('paypal', false, '', '');
  };

  const handlePaypalRestSave: PayPalButtonOnApprove = (data: OnApproveData) => {
    return new Promise<OnApproveData>((resolve, reject) => {
      // this is because Paypal does not return the orderID for REST invocations
      const orderID =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data.orderID || ((data as any)?.vaultSetupToken as string);
      if (!orderID) {
        reject(new Error('No orderID or vaultSetupToken provided'));
        return;
      }
      resolve({ ...data, orderID, payerID: data.payerID });
    })
      .then(({ orderID, payerID }) => {
        return paymentsApi.savePayPalV2({
          orderID,
          payerID,
        });
      })
      .then(() => onPaypalAddSuccess('rest'))
      .catch((error) => {
        setOverlayEvent('add paypal error');
        const errorCode = getPaymentsErrorCode(error);
        newrelic.addPageAction('paypal-rest-add', {
          status: 'failure',
          error: errorCode || error.message || 'unknown error',
        });

        onActionFailure('', ERR_UNABLE_TO_SAVE.message);
        pushErrorEvent(
          'add paypal card',
          'inline',
          errorCode || 'n/a',
          ERR_UNABLE_TO_SAVE.message,
        );
      });
  };

  const handlePaypalSoapSave: PayPalButtonOnApprove = (data: OnApproveData) => {
    return paymentsApi
      .paypalSave({ orderID: data.orderID, payerID: data.payerID }, isB2B)
      .then(() => onPaypalAddSuccess('soap'))
      .catch((error) => {
        const errorCode = getPaymentsErrorCode(error);

        newrelic.addPageAction('paypal-soap-add', {
          status: 'failure',
          error: errorCode || error.message || 'unknown error',
        });
        onActionFailure('', ERR_UNABLE_TO_SAVE.message);
        pushErrorEvent(
          'add paypal card',
          'inline',
          errorCode || 'n/a',
          ERR_UNABLE_TO_SAVE.message,
        );
      });
  };

  const handlePaypalEdit = (
    { orderID, payerID }: OnApproveData,
    paymentCard: IB2BPaymentCard | undefined,
  ) => {
    return paymentsApi
      .paypalSave({ orderID, payerID, paymentCard }, isB2B)
      .then(() => {
        onPaypalEditSuccess(paymentCard?.paypalAccountId);
      })
      .catch((error) => {
        const errorCode = getPaymentsErrorCode(error);

        newrelic.addPageAction('paypal-edit', {
          status: 'failure',
          error: errorCode || error.message || 'unknown error',
        });
        onActionFailure(
          paymentCard?.paypalAccountId || '',
          ERR_UNABLE_TO_SAVE.message,
        );
        pushErrorEvent(
          'edit paypal card',
          'inline',
          errorCode || 'n/a',
          ERR_UNABLE_TO_SAVE.message,
        );
      });
  };

  const handlePaypalError = (paypalAccountId?: string) => {
    onActionFailure(paypalAccountId, ERR_GENERIC.message);
  };

  const onChangePage = (newPageNumber: number) => {
    triggerGetPayments(newPageNumber);
  };

  const triggerGetPayments = (pageNumber: number) => {
    getPaymentCards(CardType.PAYPAL_ACCOUNT, false, false, {
      page: pageNumber,
    });
  };

  const alert = alertMessage && (
    <Alert key="alert" status={alertMessage.alertStatus} onClose={resetAlert}>
      {alertMessage.text}
    </Alert>
  );

  const paypalRestEnabled = (
    <PayPalButtons
      fundingSource="paypal"
      disabled={isSavedPaypal}
      style={{
        label: 'paypal',
        height: 44,
      }}
      createVaultSetupToken={handlePaypalInitv2}
      onApprove={handlePaypalRestSave}
      onError={() => handlePaypalError()}
    />
  );

  const paypalRestDisabled = (
    <PayPalButtons
      fundingSource="paypal"
      disabled={isSavedPaypal}
      style={{
        label: 'paypal',
        height: 44,
      }}
      createOrder={handlePaypalInit}
      onApprove={handlePaypalSoapSave}
      onError={() => handlePaypalError()}
    />
  );

  const getPaypalList = () => {
    const paypalList =
      paypalCards?.paymentCards?.map((card, cardIndex) => (
        <div key={card.paymentId}>
          {isMobile &&
            alertMessage &&
            !hasDeleteIndex &&
            alertMessage.paymentId === card.paypalAccountId && (
              <div className="sui-mb-8">{alert}</div>
            )}
          <PaypalCard
            key={card.paymentId}
            card={card}
            isB2B={isB2B}
            index={cardIndex}
            onActionFailure={onActionFailure}
            onActionSuccess={onActionSuccess}
            handlePaypalEdit={handlePaypalEdit}
            handlePaypalError={handlePaypalError}
          />
        </div>
      )) || [];

    if (isMobile && hasDeleteIndex)
      paypalList.splice(
        deletedCardIndex,
        0,
        <div className="-sui-mb-4">{alert}</div>,
      );
    return paypalList;
  };

  useEffect(() => {
    return () => {
      setAlertMessage(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Spinner active={loading || isPending} />
      <div className="sui-grid sui-grid-cols-1 sui-gap-y-6 md:sui-gap-y-8 md:sui-grid-cols-3 md:sui-gap-x-8 sui-mb-8 md:sui-mb-10">
        <div className="md:sui-col-span-2 sui-order-3 md:sui-order-1">
          <Typography weight="display" variant="h2" uppercase>
            PAYPAL ({paginationInfo?.totalPayPalAccounts})
          </Typography>
        </div>
        <div className="sui-order-1">
          {!isPending && (
            <div className="sui-mb-2">
              <Typography height="tight">Add New PayPal Account</Typography>
            </div>
          )}
          {isPaypalRestEnabled ? paypalRestEnabled : paypalRestDisabled}
        </div>
        {isSavedPaypal && (
          <div className="sui-order-2 md:sui-order-1 md:sui-col-span-3">
            <div className="-sui-mb-4">
              <Alert status="info">
                You may only have one PayPal account PER USER. To add another,
                delete your current account first.
              </Alert>
            </div>
          </div>
        )}
      </div>
      {alertMessage &&
        (!isMobile ||
          (alertMessage.paymentId === undefined && !hasDeleteIndex)) && (
          <div className="sui-mb-8">{alert}</div>
        )}
      {hasNoPaymentCards(paypalCards) && (
        <NoPaymentCardsMessage tenderType={CardType.PAYPAL_ACCOUNT} />
      )}
      <div className="sui-grid sui-grid-cols-1 sui-gap-y-8 sui-gap-x-8 md:sui-grid-cols-2 2xl:sui-gap-y-16 2xl:sui-grid-cols-3">
        {getPaypalList()}
      </div>
      {totalPages > 1 && (
        <div className="sui-w-full sui-justify-center sui-p-5 sui-flex">
          <Pagination
            nPages={totalPages}
            onChangePage={(newPage) => {
              onChangePage(newPage);
              setAlertMessage(null);
              scrollToTop();
            }}
            page={currentPageNumber}
          />
        </div>
      )}
    </>
  );
};

export const PayPalList = () => (
  <PayPalScriptProvider options={{ clientId: payPalClientId, vault: true }}>
    <PayPalListContent />
  </PayPalScriptProvider>
);
